import React, { useContext, useCallback } from 'react';
import { connect } from 'react-redux';

import { AppContext } from '@prisjakt/ui-app-context';
import Menu from '@prisjakt/ui-menu';
import { TitleSmallText, FontEmphasis } from '@prisjakt/ui/text/variants';
import { Column } from '@prisjakt/ui/list/variants';
import AnalyticsIcon from 'mdi-react/PollBoxIcon';

import { Grow, Position } from '@prisjakt/ui-menu/src/types';

import { AppState } from '../../../reducers';
import { logoutRequest } from '../actions';
import {
  closeLists as closeListsAction,
  selectList as selectListAction,
} from '../../sidebar/actions/lists';
import { User } from '../../../types';

import * as S from './styles';

type OwnProps = {
  position: Position;
  grow: Grow;
  toggleElement: React.ReactElement;
  toggleElementStyles?: {};
  toggleElementActiveStyles?: {};
  style?: {};
  className?: string;
};

type StateProps = {
  me: User;
};

type DispatchProps = {
  dispatchLogoutRequest: (message: string) => void;
  closeLists: () => void;
  selectList: (id: number | null) => void;
};

type Props = OwnProps & DispatchProps & StateProps;

const AccountMenu: React.FC<Props> = ({
  dispatchLogoutRequest,
  position,
  grow,
  toggleElement,
  toggleElementStyles,
  toggleElementActiveStyles,
  style,
  className,
  closeLists,
  selectList,
  me,
}) => {
  const { t } = useContext(AppContext);

  const onLogout = useCallback(() => {
    selectList(null);
    closeLists();
    dispatchLogoutRequest(t('account', 'logout.successful'));
  }, [selectList, closeLists, dispatchLogoutRequest, t]);

  return (
    <>
      <Menu
        className={className}
        position={position}
        grow={grow}
        width="200px"
        toggleElement={toggleElement}
        toggleElementStyles={toggleElementStyles}
        toggleElementActiveStyles={toggleElementActiveStyles}
        closeOnClick
        style={style}
      >
        <S.ProfileLinks gap={1}>
          <S.MenuItemLink to="/account/profile">
            <TitleSmallText>{t('account', 'profile.title')}</TitleSmallText>
          </S.MenuItemLink>

          <S.MenuItemLink to="/account/profile/settings">
            <TitleSmallText>{t('account', 'menu.settings')}</TitleSmallText>
          </S.MenuItemLink>

          {me?.analyticsAccess && (
            <Column gap={0.5}>
              <TitleSmallText emphasis={FontEmphasis.MEDIUM}>
                <S.AnalyticsWrapper gap={0.25}>
                  <AnalyticsIcon /> Analytics
                </S.AnalyticsWrapper>
              </TitleSmallText>

              {me.analyticsAccess.stores &&
                me.analyticsAccess.stores?.map(
                  store =>
                    store && (
                      <S.MenuItemLink
                        key={store.name}
                        href={store.url}
                        data-test="MenuItemStore"
                        as="a"
                      >
                        <TitleSmallText>{store.name}</TitleSmallText>
                      </S.MenuItemLink>
                    )
                )}
              {me.analyticsAccess.brands &&
                me.analyticsAccess.brands?.map(
                  brand =>
                    brand && (
                      <S.MenuItemLink
                        key={brand.name}
                        href={brand.url}
                        data-test="MenuItemBrand"
                        as="a"
                      >
                        <TitleSmallText>{brand.name}</TitleSmallText>
                      </S.MenuItemLink>
                    )
                )}
            </Column>
          )}

          <S.Buttons>
            <S.AccountButton fullWidth onClick={onLogout}>
              {t('account', 'menu.logout-button')}
            </S.AccountButton>
          </S.Buttons>
        </S.ProfileLinks>
      </Menu>
    </>
  );
};

const mapStateToProps = (state: AppState) => ({
  me: state.areas.account.me,
});

const dispatchActions = {
  dispatchLogoutRequest: logoutRequest,
  closeLists: closeListsAction,
  selectList: selectListAction,
};

export default connect(mapStateToProps, dispatchActions)(AccountMenu);
